'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

interface PathProgress {
  path_id: number;
  progress_percent: number;
  completed: boolean;
  current_item: number;
}

interface LearningPath {
  id: number;
  title: string;
  difficulty: string;
  content_items: any[];
  estimated_hours: number;
  user_progress: PathProgress | null;
}

export default function LearningPathsSection() {
  const [paths, setPaths] = useState<LearningPath[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPaths();
  }, []);

  async function fetchPaths() {
    try {
      const response = await fetch('/api/secure/learning-paths?limit=6', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Filter to show only paths with progress
        const activePaths = data.data.paths.filter((p: LearningPath) => p.user_progress);
        setPaths(activePaths.slice(0, 3));
      }
    } catch (error) {
      console.error('Failed to fetch learning paths:', error);
    } finally {
      setIsLoading(false);
    }
  }

  function getDifficultyColor(difficulty: string) {
    switch (difficulty) {
      case 'beginner':
        return 'text-green-400';
      case 'intermediate':
        return 'text-accent';
      case 'advanced':
        return 'text-red-400';
      default:
        return 'text-foreground';
    }
  }

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-6 bg-accent-muted rounded w-48 mb-4"></div>
        <div className="space-y-3">
          <div className="h-24 bg-accent-muted rounded"></div>
          <div className="h-24 bg-accent-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (paths.length === 0) {
    return (
      <div>
        <h3 className="text-lg font-bold text-foreground mb-4">Your Learning Paths</h3>
        <div className="bg-surface border border-border rounded-lg p-6 text-center">
          <div className="text-4xl mb-3">📚</div>
          <p className="text-foreground-secondary text-sm mb-3">
            No active learning paths yet
          </p>
          <Link
            href="/paths"
            className="text-accent hover:text-foreground transition-colors text-sm font-medium"
          >
            Browse Learning Paths →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-foreground">Your Learning Paths</h3>
        <Link
          href="/paths"
          className="text-accent hover:text-foreground transition-colors text-sm"
        >
          View all →
        </Link>
      </div>

      <div className="space-y-3">
        {paths.map((path) => (
          <Link
            key={path.id}
            href={`/paths/${path.id}`}
            className="block bg-surface border border-border rounded-lg p-4 hover:border-border-muted0 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h4 className="font-semibold text-foreground text-sm mb-1 line-clamp-1">
                  {path.title}
                </h4>
                <div className="flex items-center gap-2 text-xs">
                  <span className={getDifficultyColor(path.difficulty)}>
                    {path.difficulty}
                  </span>
                  <span className="text-foreground-tertiary">•</span>
                  <span className="text-foreground-tertiary">
                    {path.content_items.length} items
                  </span>
                </div>
              </div>
              {path.user_progress && (
                <div className="text-sm font-medium text-accent ml-2">
                  {path.user_progress.progress_percent}%
                </div>
              )}
            </div>

            {path.user_progress && (
              <div className="w-full bg-background rounded-full h-2">
                <div
                  className="bg-accent h-2 rounded-full transition-all"
                  style={{ width: `${path.user_progress.progress_percent}%` }}
                ></div>
              </div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
